import { useMemo, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import type { usePortalAccess } from '../../hooks/usePortalAccess'
import type { PortalRoleDrafts } from '../../hooks/usePortalRoleDrafts'
import { formatShortDate } from '../../lib/months'
import {
  DEFAULT_GRANT_ORDER,
  DEFAULT_GRANT_SORT,
  EMPTY_ADD_DRAFT,
  grantErrorMessage,
  hasGrant,
  rowWriteErrorMessage,
  sortAndFilterGrants,
  type AddPortalUserDraft,
  type GrantSortKey,
  type GrantSortOrder,
} from '../../lib/portalAccess'
import { PORTAL_ROLES, type PortalAccessGrant, type PortalRole } from '../../lib/types'
import { Avatar } from '../ui/Avatar'
import { Button } from '../ui/Button'
import { Card, CardBody, CardHeader } from '../ui/Card'
import { FilterLabel } from '../ui/FilterLabel'
import { InfoTip } from '../ui/InfoTip'
import { QueryState } from '../ui/QueryStateCard'
import { Select } from '../ui/Select'
import { AddPortalUserModal } from './AddPortalUserModal'
import { PortalAccessConfirmModal, type PortalAccessConfirm } from './PortalAccessConfirmModal'

function BinIcon() {
  return (
    <svg viewBox="0 0 24 24" width="16" height="16" stroke="var(--danger)" fill="none" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <polyline points="3 6 5 6 21 6" />
      <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
      <line x1="10" y1="11" x2="10" y2="17" />
      <line x1="14" y1="11" x2="14" y2="17" />
    </svg>
  )
}

// The prototype's `.skill-remove-btn`: 6px padding, 6px radius, a 16px glyph,
// a `--danger-bg` hover.
const REMOVE_BUTTON =
  'inline-flex items-center justify-center p-1.5 rounded-md bg-transparent border-0 cursor-pointer transition-colors hover:bg-[var(--danger-bg)] disabled:opacity-30 disabled:pointer-events-none'

const TH = 'text-left font-semibold text-[11px] text-[var(--text-tertiary)] uppercase tracking-[.5px] px-3 py-2.5 border-b border-[var(--border)]'
const TD = 'p-3 border-b border-[var(--border-light)] align-middle'

/** Settings › Access's one card (MS-997 D4) — the prototype's "Subscription
 * Portal Access" card (`#tab-settings-access`): "+ Add user", the search /
 * Sort by / Order bar (no erasers, as in the prototype), and the table:
 * avatar + email, the Permission select, the Added date and the remove
 * button. Grant and revoke go through the plain confirmation and apply at
 * once. No banner.
 *
 * The Permission select no longer applies at once (the prototype's
 * §1-ctrl-scope): its pick is staged in the page's draft (`roles`,
 * hooks/usePortalRoleDrafts.ts) and saved by the page header's Save, which
 * toasts — ARCHITECTURE_DECISIONS.md §64, at the user's request. While a save
 * runs, the selects and remove buttons are inert.
 *
 * Not drawn in the prototype, each an ADR §57 decision: the loading and
 * error states, the last-Admin refusal (Q2) and the Add modal's server-error
 * line (Q28). There is no password reset (§61): a user who forgot their
 * password uses "Forgot password" on the Cognito sign-in page. */
export function AccessCard({
  access,
  roles,
}: Readonly<{ access: ReturnType<typeof usePortalAccess>; roles: PortalRoleDrafts }>) {
  const { t, i18n } = useTranslation()
  const { grants, grant, revoke } = access
  const { notice: error, setNotice: setError, saving } = roles
  const [search, setSearch] = useState('')
  const [sortKey, setSortKey] = useState<GrantSortKey>(DEFAULT_GRANT_SORT)
  const [order, setOrder] = useState<GrantSortOrder>(DEFAULT_GRANT_ORDER)
  const [addDraft, setAddDraft] = useState<AddPortalUserDraft | null>(null)
  const [confirm, setConfirm] = useState<(PortalAccessConfirm & { target?: PortalAccessGrant }) | null>(null)

  const rows = useMemo(() => grants.data ?? [], [grants.data])
  const visible = useMemo(() => sortAndFilterGrants(rows, search, sortKey, order), [rows, search, sortKey, order])

  function openAdd() {
    setError(null)
    setAddDraft(EMPTY_ADD_DRAFT)
  }

  function onConfirmed() {
    const pending = confirm
    // The prototype closes the dialogue at once (`ok` handler); the change
    // lands when the request settles, and an error shows where it belongs.
    setConfirm(null)
    if (!pending) return
    if (pending.mode === 'grant') {
      const { email, role } = pending
      grant.mutate(
        { email, role },
        {
          onError: (err) => {
            const message = grantErrorMessage(err, email, t)
            if (message) setAddDraft({ email, role, error: message })
          },
        }
      )
    } else if (pending.mode === 'revoke' && pending.target) {
      revoke.mutate(pending.target, { onError: (err) => setError(rowWriteErrorMessage(err, t)) })
    }
  }

  let body
  if (grants.isPending) {
    body = <QueryState state="pending" />
  } else if (grants.isError) {
    body = <QueryState state="error" message={t('settings.access.loadFailed')} onRetry={() => grants.refetch()} />
  } else {
    body = (
      <table className="w-full border-collapse text-[13px]" data-testid="portal-access-table">
        <thead>
          <tr>
            <th className={TH}>{t('settings.access.columns.email')}</th>
            <th className={`${TH} relative`}>
              {t('settings.access.columns.permission')}
              <InfoTip
                placement="below"
                align="left"
                width={330}
                className="ml-1"
                text={<Trans i18nKey="settings.access.permissionNote" components={{ strong: <strong />, br: <br /> }} />}
              />
            </th>
            <th className={TH}>{t('settings.access.columns.added')}</th>
            <th className={TH} aria-label={t('settings.access.columns.actions')} />
          </tr>
        </thead>
        <tbody>
          {visible.map((g) => (
            <tr key={g.id} className="[&:last-child>td]:border-b-0">
              <td className={TD}>
                <div className="flex items-center gap-2.5">
                  <Avatar size="sm" />
                  <div className="text-[13.5px] font-semibold text-[var(--text-primary)] leading-[1.2]">{g.email}</div>
                </div>
              </td>
              <td className={TD}>
                <Select
                  variant="role"
                  className="inline-block"
                  aria-label={t('settings.access.permissionAria')}
                  value={roles.roleOf(g)}
                  disabled={saving}
                  onChange={(e) => roles.setRole(g, e.target.value as PortalRole)}
                >
                  {PORTAL_ROLES.map((r) => (
                    <option key={r} value={r}>
                      {t(`settings.roles.${r}`)}
                    </option>
                  ))}
                </Select>
              </td>
              <td className={TD}>{g.grantedAt ? formatShortDate(g.grantedAt, i18n.language) : t('common.dash')}</td>
              <td className={TD}>
                <button
                  type="button"
                  title={t('settings.access.removeAria')}
                  aria-label={t('settings.access.removeAria')}
                  className={REMOVE_BUTTON}
                  disabled={saving}
                  onClick={() => {
                    setError(null)
                    setConfirm({ mode: 'revoke', email: g.email, target: g })
                  }}
                >
                  <BinIcon />
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    )
  }

  return (
    <>
      <Card collapsible>
        <CardHeader
          title={t('settings.access.cardTitle')}
          subtitle={t('settings.access.cardCaption')}
          actions={
            <Button variant="ghost" onClick={openAdd} className="!text-[12px] !px-3 !py-[5px]">
              {t('settings.access.addUser')}
            </Button>
          }
        />
        <CardBody>
          <div className="flex gap-3 items-end flex-nowrap mb-5">
            <div className="flex-[1_1_220px] min-w-0 flex flex-col gap-1">
              <FilterLabel label={t('settings.access.searchLabel')} />
              <input
                type="text"
                aria-label={t('settings.access.searchLabel')}
                placeholder={t('settings.access.searchPlaceholder')}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full h-8 border border-[var(--border)] rounded-md px-2.5 py-1.5 text-[12.5px] bg-[var(--bg-input)] text-[var(--text-primary)] transition-[border-color,box-shadow] focus:outline-none focus:border-[var(--border-focus)] focus:shadow-[0_0_0_3px_var(--accent-subtle)]"
              />
            </div>
            <div className="flex-[0_0_150px] min-w-0 flex flex-col gap-1">
              <FilterLabel label={t('settings.access.sortByLabel')} />
              <Select
                aria-label={t('settings.access.sortByLabel')}
                value={sortKey}
                onChange={(e) => setSortKey(e.target.value as GrantSortKey)}
              >
                <option value="email">{t('settings.access.sort.email')}</option>
                <option value="added">{t('settings.access.sort.added')}</option>
              </Select>
            </div>
            <div className="flex-[0_0_150px] min-w-0 flex flex-col gap-1">
              <FilterLabel label={t('settings.access.orderLabel')} />
              <Select
                aria-label={t('settings.access.orderLabel')}
                value={order}
                onChange={(e) => setOrder(e.target.value as GrantSortOrder)}
              >
                <option value="asc">{t('settings.access.order.asc')}</option>
                <option value="desc">{t('settings.access.order.desc')}</option>
              </Select>
            </div>
          </div>
          {body}
          {error && (
            <div role="alert" data-testid="portal-access-notice" className="mt-3 text-[12px] text-[var(--danger)]">
              {error}
            </div>
          )}
        </CardBody>
      </Card>

      {addDraft && (
        <AddPortalUserModal
          initial={addDraft}
          hasGrant={(email) => hasGrant(rows, email)}
          onClose={() => setAddDraft(null)}
          onSubmit={(draft) => {
            setAddDraft(null)
            setConfirm({ mode: 'grant', email: draft.email, role: draft.role })
          }}
        />
      )}
      {confirm && (
        <PortalAccessConfirmModal confirm={confirm} onConfirm={onConfirmed} onCancel={() => setConfirm(null)} />
      )}
    </>
  )
}
